import { useContext, useEffect, useState } from 'preact/hooks';
import { SessionContext } from '../app.jsx';
import { get, post, put } from '../api.js';
import { navigate } from '../router.js';
import { Field, useForm } from '../ui/Field.jsx';
import { Tag } from '../ui/Tag.jsx';
import { fmtRange, isDate, todayBelgrade } from '../../lib/dates.js';

// The four states (decision 5), in this order everywhere.
const STATES = [['free', 'slobodno', 'Slobodno'], ['busy', 'zauzeto', 'Zauzeto'], ['damaged', 'osteceno', 'Oštećeno'], ['missing', 'nestalo', 'Nestalo']];
const whereLine = (w) => `${w.tour}, ${fmtRange(w.start_date, w.end_date)} — ${w.guide}, ${w.qty}`;
const EMPTY_ITEM = { name: '', total: '' };

function useItems(day) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { tick } = useContext(SessionContext);
  const load = () => get(`/items?date=${day}`).then((r) => { setData(r); setError(null); }, (e) => setError(e.message));
  useEffect(() => { load(); }, [day, tick]);
  return { items: data?.date === day ? data.items : null, error, load };
}

// Guide: every item as a card, "now".
function EquipmentGuide() {
  const { items, error } = useItems(todayBelgrade());
  return (
    <>
      <h1>Oprema</h1>
      {error && <p class="alert" role="alert">{error}</p>}
      {items && items.length === 0 && <p class="empty">Kancelarija još nije unela opremu.</p>}
      {items?.map((i) => (
        <article key={i.id} class="card item-card" data-testid={`item-${i.id}`}>
          <div class="card-head">
            <h3>{i.name}</h3>
            <span class="num">{`Ukupno ${i.total}`}</span>
          </div>
          <div class="tags">
            {STATES.map(([k, kind, label]) => (
              <span key={k} class={i[k] === 0 ? 'dim' : undefined}><Tag kind={kind}>{`${label} ${i[k]}`}</Tag></span>
            ))}
          </div>
          {i.where.length > 0 && (
            <ul class="plain-list where">{i.where.map((w, n) => <li key={n}>{whereLine(w)}</li>)}</ul>
          )}
        </article>
      ))}
    </>
  );
}

function ItemFields({ bind }) {
  return (
    <>
      <Field label="Naziv"><input {...bind('name')} autocomplete="off" maxlength="80" required /></Field>
      <Field label="Ukupno"><input {...bind('total')} type="number" inputmode="numeric" min="0" max="10000" step="1" required /></Field>
    </>
  );
}

function EditItem({ item, onSaved, onCancel }) {
  const [f, bind] = useForm({ name: item.name, total: String(item.total) });
  const [error, setError] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await put(`/items/${item.id}`, f);
      await onSaved();
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <tr>
      <td colSpan={7} class="cell-form">
        <form class="inline-form" aria-label="Izmeni stavku" onSubmit={save}>
          <ItemFields bind={bind} />
          {error && <p class="alert" role="alert">{error}</p>}
          <div class="actions">
            <button class="btn">Sačuvaj</button>
            <button type="button" class="btn-quiet" onClick={onCancel}>Odustani</button>
          </div>
        </form>
      </td>
    </tr>
  );
}

// "Vrati u upotrebu": how many damaged and missing pieces are back, all of them by default.
function ReturnForm({ item, onSaved, onCancel }) {
  const [f, bind] = useForm({ damaged: String(item.damaged), missing: String(item.missing) });
  const [error, setError] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await post(`/items/${item.id}/return`, f);
      await onSaved();
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <form class="return-form" aria-label={`Vrati u upotrebu: ${item.name}`} onSubmit={save}>
      <Field label="Oštećeno"><input {...bind('damaged')} type="number" inputmode="numeric" min="0" max={item.damaged} step="1" required /></Field>
      <Field label="Nestalo"><input {...bind('missing')} type="number" inputmode="numeric" min="0" max={item.missing} step="1" required /></Field>
      {error && <p class="alert" role="alert">{error}</p>}
      <div class="actions">
        <button class="btn">Vrati</button>
        <button type="button" class="btn-quiet" onClick={onCancel}>Odustani</button>
      </div>
    </form>
  );
}

// SC-008 (office): the table on a chosen day, new item, edit, return to use.
function EquipmentOffice({ query }) {
  const day = isDate(query.get('date')) ? query.get('date') : todayBelgrade();
  const { items, error, load } = useItems(day);
  const [editing, setEditing] = useState(null);
  const [returning, setReturning] = useState(null);
  const [add, bindAdd, setAdd] = useForm(EMPTY_ITEM);
  const [addError, setAddError] = useState(null);

  const addItem = async (e) => {
    e.preventDefault();
    setAddError(null);
    try {
      await post('/items', add);
      setAdd(EMPTY_ITEM);
      await load();
    } catch (err) {
      setAddError(err.message);
    }
  };
  const done = (close) => async () => { close(null); await load(); };

  return (
    <>
      <div class="page-head">
        <h1>Oprema</h1>
        <label class="field day-field">
          <span>Stanje na dan</span>
          <input type="date" value={day} required
            onInput={(e) => isDate(e.currentTarget.value) && navigate(`/oprema?date=${e.currentTarget.value}`, { replace: true })} />
        </label>
      </div>
      {error && <p class="alert" role="alert">{error}</p>}
      {items && items.length === 0 && <p class="empty">Još nema opreme. Dodaj prvu stavku: Nova stavka.</p>}
      {items && items.length > 0 && (
        <table class="stack items">
          <thead>
            <tr>
              <th>Stavka</th><th class="num">Ukupno</th>
              {STATES.map(([k, , label]) => <th key={k}>{label}</th>)}
              <th>Radnje</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (editing === i.id
              ? <EditItem key={i.id} item={i} onCancel={() => setEditing(null)} onSaved={done(setEditing)} />
              : (
                <tr key={i.id}>
                  <td data-label="Stavka"><strong>{i.name}</strong></td>
                  <td data-label="Ukupno" class="num">{i.total}</td>
                  {STATES.map(([k, kind, label]) => (
                    <td key={k} data-label={label} data-testid={kind} class="state">
                      <div>
                        <span class={'num count count-' + kind + (i[k] === 0 ? ' dim' : '')}>{i[k]}</span>
                        {k === 'busy' && i.where.length > 0 && (
                          <ul class="plain-list where">{i.where.map((w, n) => <li key={n}>{whereLine(w)}</li>)}</ul>
                        )}
                      </div>
                    </td>
                  ))}
                  <td class="cell-action">
                    {returning === i.id
                      ? <ReturnForm item={i} onCancel={() => setReturning(null)} onSaved={done(setReturning)} />
                      : (
                        <div class="actions">
                          <button class="btn-quiet" onClick={() => { setEditing(i.id); setReturning(null); }}>Izmeni</button>
                          {(i.damaged > 0 || i.missing > 0) && (
                            <button class="btn-quiet" onClick={() => setReturning(i.id)}>Vrati u upotrebu</button>
                          )}
                        </div>
                      )}
                  </td>
                </tr>
              )))}
          </tbody>
        </table>
      )}
      <section class="section new-item" aria-labelledby="nova-stavka">
        <h2 id="nova-stavka">Nova stavka</h2>
        <form class="panel inline-form" aria-label="Nova stavka" onSubmit={addItem}>
          <ItemFields bind={bindAdd} />
          {addError && <p class="alert" role="alert">{addError}</p>}
          <div class="actions"><button class="btn">Dodaj stavku</button></div>
        </form>
      </section>
    </>
  );
}

export function Equipment({ query }) {
  const { user } = useContext(SessionContext);
  return user.role === 'office' ? <EquipmentOffice query={query} /> : <EquipmentGuide />;
}
